import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Form, InputNumber, Radio, Space } from "antd";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomModal, CustomSelect } from "sipli-fleet-core/components";
import { EDIT_ICON } from "src/assets/svgs";
import {
  useMutationUpdateVehicleDetails,
  useQueryDeviceDetailsLookup,
} from "src/queries/deviceDetails.queries";
import type { LookupValue, VehicleDetail } from "src/services/deviceDetailsService";
import DetailsCard, { DetailsReadOnlyField } from "./DetailsCard";
import { haveFormValuesChanged, toFloat, toInteger, toOptions, valueId } from "./details.helpers";
import EpochDate from "../../../../../../sipli-fleet-core/components/EpochDate/EpochDate";

const EditIcon = EDIT_ICON as unknown as ComponentType<{ className?: string }>;

interface TechnicalFormValues {
  odometer?: number;
  enginePower?: number;
  enginePowerUnit?: string;
  engineCapacity?: number;
  engineType: "internal_combustion" | "electric";
  fuelType?: string | number;
  fuelTankSize?: number;
  adblueTankSize?: number;
  avgFuelConsumption?: number;
  batteryCapacity?: number;
  energyConsumption?: number;
  chargingPower?: number;
  emissionClass?: string | number;
  nrmmStage?: string | number;
  co2Emissions?: number;
  avgEmissions?: number;
}

const getTechnicalFormValues = (vehicle: VehicleDetail): TechnicalFormValues => ({
  odometer: vehicle.odometer,
  enginePower: vehicle.enginePower,
  enginePowerUnit: vehicle.enginePowerUnit,
  engineCapacity: vehicle.engineCapacity,
  engineType: vehicle.fuelType?.code === "ELECTRIC" ? "electric" : "internal_combustion",
  fuelType: valueId(vehicle.fuelType),
  fuelTankSize: vehicle.fuelTankSize,
  adblueTankSize: vehicle.adblueTankSize,
  avgFuelConsumption: vehicle.avgFuelConsumption,
  batteryCapacity: vehicle.batteryCapacity,
  energyConsumption: vehicle.energyConsumption,
  chargingPower: vehicle.chargingPower,
  emissionClass: valueId(vehicle.emissionClass) ?? vehicle.emissionClass?.code,
  nrmmStage: valueId(vehicle.nrmmStage) ?? vehicle.nrmmStage?.code,
  co2Emissions: vehicle.co2Emissions,
  avgEmissions: vehicle.avgEmissions,
});

const TechnicalInformationCard = ({ vehicle }: { vehicle: VehicleDetail }) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<TechnicalFormValues>();
  const [initialValues, setInitialValues] = useState(() => getTechnicalFormValues(vehicle));
  const mutation = useMutationUpdateVehicleDetails();
  const [isOdometerOpen, setIsOdometerOpen] = useState(false);
  const [odometerDraft, setOdometerDraft] = useState<number | undefined>(vehicle.odometer);
  const powerUnits = useQueryDeviceDetailsLookup("powerUnits");
  const fuelTypes = useQueryDeviceDetailsLookup("fuelTypes");
  const emissions = useQueryDeviceDetailsLookup("emissions");
  const nrmmStages = useQueryDeviceDetailsLookup("nrmmStages");
  const engineType = Form.useWatch("engineType", form) ?? "internal_combustion";

  const electricFuel = useMemo(
    () => fuelTypes.data?.find((item) => item.code === "ELECTRIC"),
    [fuelTypes.data]
  );

  useEffect(() => {
    const values = getTechnicalFormValues(vehicle);
    setInitialValues(values);
    form.resetFields();
    form.setFieldsValue(values);
    setOdometerDraft(vehicle.odometer);
  }, [form, vehicle]);

  useEffect(() => {
    if (!electricFuel) return;

    const electricFuelValue = valueId(electricFuel);
    if (engineType === "electric") {
      form.setFieldValue("fuelType", electricFuelValue);
      return;
    }

    const currentFuelType = form.getFieldValue("fuelType");
    if (currentFuelType === electricFuelValue || currentFuelType === electricFuel.code) {
      form.setFieldValue("fuelType", undefined);
    }
  }, [electricFuel, engineType, form]);

  const handleSave = async () => {
    if (vehicle.id === undefined) return;
    const values = await form.validateFields();
    try {
      await mutation.mutateAsync({
        id: vehicle.id,
        payload: {
          ...values,
          fuelTypeId:
            engineType === "electric" ? (valueId(electricFuel) ?? null) : (values.fuelType ?? null),
          emissionClass: values.emissionClass,
          nrmmStage: values.nrmmStage,
          adblueTankSize: toInteger(values.adblueTankSize),
          avgEmissions: toInteger(values.avgEmissions),
          avgFuelConsumption: toFloat(values.avgFuelConsumption),
          batteryCapacity: toInteger(values.batteryCapacity),
          chargingPower: toInteger(values.chargingPower),
          co2Emissions: toFloat(values.co2Emissions),
          energyConsumption: toInteger(values.energyConsumption),
          engineCapacity: toInteger(values.engineCapacity),
          enginePower: toFloat(values.enginePower),
          enginePowerUnit: values.enginePowerUnit ?? vehicle.enginePowerUnit ?? null,
          fuelTankSize: toInteger(values.fuelTankSize),
          odometer: toInteger(values.odometer),
          fuelType: undefined,
          engineType: undefined,
        },
      });
      setInitialValues(values);
      message.success(t("technical_information_saved_successfully"));
    } catch {
      message.error(t("failed_to_save_technical_information"));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setOdometerDraft(initialValues.odometer);
    setIsOdometerOpen(false);
  };

  const translatedFuelTypes = (fuelTypes.data ?? [])
    .filter((item) => engineType === "electric" || item.code !== "ELECTRIC")
    .map((item): LookupValue => {
      const key = `fuel_${item.code}`;
      const translated = t(key);
      return { ...item, name: translated === key ? item.code : translated };
    });
  const odometerValue = Form.useWatch("odometer", form);

  return (
    <DetailsCard title={t("technical_information")}>
      <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
        <Form.Item name="odometer" hidden>
          <InputNumber className="common-input" />
        </Form.Item>
        <div className="mb-4 mt-4 flex flex-col gap-2 sm:flex-row">
          <div className="h-[103.85px] w-full rounded-[4px] border border-gray3 bg-gray1 p-4 sm:min-w-0 sm:flex-1">
            <div className="border-b border-gray4 pb-2 text-[14px] font-bold leading-[18px] text-black">
              {t("km_can")}: {formatNumber(vehicle.kmFromCan ?? 0, i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
            </div>
            <div className="flex items-start justify-between gap-3 pt-3">
              <div>
                <div className="text-[14px] font-bold leading-[18px] text-black">
                  {t("km_gps")}: {formatNumber(odometerValue ?? vehicle.odometer ?? 0, i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                </div>
                <div className="text-[12.44px] font-medium leading-[13px] tracking-[-0.2px] text-gray6">
                  {t("last_modified_on")} <EpochDate date={vehicle.odometerUpdated} />
                </div>
              </div>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("edit")}
                icon={<EditIcon />}
                onClick={() => {
                  setOdometerDraft(form.getFieldValue("odometer") ?? vehicle.odometer);
                  setIsOdometerOpen(true);
                }}
              />
            </div>
          </div>
          <div className="h-[103.85px] w-full rounded-[4px] border border-gray3 bg-gray1 p-4 sm:min-w-0 sm:flex-1">
            <div className="border-b border-gray4 pb-2 text-[14px] font-bold leading-[18px] text-black">
              {t("engine_hours_can")}: {vehicle.engineHoursCan ?? "--.--"} km
            </div>
            <div className="pt-4.5 text-[14px] font-bold leading-[18px] text-gray6">
              <span>{t("engine_hours_gps")}:</span>{" "}
              <span>{vehicle.engineHoursGps ?? "--.--"} km</span>
            </div>
          </div>
        </div>
        <div className="mb-5 grid grid-cols-1 gap-x-4 border-b border-gray4 pb-5 md:grid-cols-2">
          <Form.Item label={t("power")}>
            <Space.Compact block>
              <Form.Item name="enginePower" noStyle>
                <InputNumber
                  className="common-input"
                  controls={false}
                  style={{ width: "76%" }}
                  placeholder={t("enter_a_number")}
                />
              </Form.Item>
              <Form.Item name="enginePowerUnit" noStyle>
                <CustomSelect
                  className="common-select"
                  containerClassName="!w-1/4"
                  placeholder={t("power")}
                  options={(powerUnits.data ?? []).map((item) => ({
                    value: item.code,
                    label: item.code,
                  }))}
                  loading={powerUnits.isLoading}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="engineCapacity" label={t("displacement")}>
            <InputNumber
              className="common-input"
              style={{ width: "100%" }}
              placeholder={t("cm3")}
            />
          </Form.Item>
        </div>

        <div className="mb-3 text-[15.75px] font-semibold leading-[20px] text-black">
          {t("fuel_tank_and_fuel_type")}
        </div>

        <div className="flex items-end gap-4 mb-2">
          <Form.Item name="engineType" className="!w-[50%]">
            <Radio.Group className="flex !w-full">
              <Radio.Button
                className="w-[50%] !rounded-l-[4px] !p-0 text-center"
                value="internal_combustion"
              >
                {t("internal_combustion")}
              </Radio.Button>
              <Radio.Button className="w-[50%] !rounded-r-[4px] !p-0 text-center" value="electric">
                {t("electric")}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="fuelType" label={t("fuel_type")} className="!w-[50%]">
            <CustomSelect
              className="common-select"
              placeholder={t("fuel_type")}
              disabled={engineType === "electric"}
              showSearch
              optionFilterProp="label"
              options={toOptions(translatedFuelTypes)}
              loading={fuelTypes.isLoading}
            />
          </Form.Item>
        </div>
        {engineType === "internal_combustion" ? (
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-3">
            {vehicle.tankCapacityL ? (
              <DetailsReadOnlyField
                label={t("tank_capacity")}
                value={`${vehicle.tankCapacityL}l`}
              />
            ) : (
              <Form.Item name="fuelTankSize" label={t("tank_capacity")}>
                <InputNumber className="common-input" style={{ width: "100%" }} placeholder="L" />
              </Form.Item>
            )}
            <Form.Item name="adblueTankSize" label={t("adblue_capacity")}>
              <InputNumber className="common-input" style={{ width: "100%" }} placeholder="L" />
            </Form.Item>
            <Form.Item name="avgFuelConsumption" label={t("average_consumption")}>
              <InputNumber
                className="common-input"
                style={{ width: "100%" }}
                placeholder="L/100km"
              />
            </Form.Item>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-3">
            <Form.Item name="batteryCapacity" label={t("battery_capacity")}>
              <InputNumber className="common-input" style={{ width: "100%" }} placeholder="kwh" />
            </Form.Item>
            <Form.Item name="energyConsumption" label={t("energy_consumption")}>
              <InputNumber
                className="common-input"
                style={{ width: "100%" }}
                placeholder="kwh/100 km"
              />
            </Form.Item>
            <Form.Item name="chargingPower" label={t("charging_power")}>
              <InputNumber className="common-input" style={{ width: "100%" }} placeholder="kw" />
            </Form.Item>
          </div>
        )}

        <div className="mt-4 mb-8 border-t border-gray4 pt-5 text-[15.75px] font-semibold leading-[20px] text-black">
          {t("emissions")}
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
          <Form.Item name="emissionClass" label={t("emission_class")}>
            <CustomSelect
              className="common-select"
              allowClear
              showSearch
              optionFilterProp="label"
              options={toOptions(emissions.data?.map((item) => ({ ...item, id: item.code })))}
              loading={emissions.isLoading}
              placeholder={t("emission_class")}
            />
          </Form.Item>
          <Form.Item name="nrmmStage" label={t("stage_classification")}>
            <CustomSelect
              className="common-select"
              allowClear
              showSearch
              optionFilterProp="label"
              options={toOptions(nrmmStages.data?.map((item) => ({ ...item, id: item.code })))}
              loading={nrmmStages.isLoading}
              placeholder={t("stage_classification")}
            />
          </Form.Item>
          <Form.Item name="co2Emissions" label={`${t("co2_emissions")} (g/km)`}>
            <InputNumber className="common-input" style={{ width: "100%" }} placeholder="g/km" />
          </Form.Item>
          <Form.Item name="avgEmissions" label={`${t("average_emissions")} (g/km)`}>
            <InputNumber className="common-input" style={{ width: "100%" }} placeholder="g/km" />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-2 pt-8">
          <CustomButton
            type="secondary"
            label={t("cancel")}
            className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px]"
            disabled={mutation.isPending}
            onClick={handleCancel}
          />
          <Form.Item noStyle shouldUpdate>
            {() => (
              <CustomButton
                htmlType="submit"
                type="primary"
                label={t("save")}
                className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px] !text-gray2"
                loading={mutation.isPending}
                disabled={
                  !haveFormValuesChanged(initialValues, form.getFieldsValue(true)) ||
                  mutation.isPending
                }
              />
            )}
          </Form.Item>
        </div>
      </Form>
      <CustomModal
        open={isOdometerOpen}
        centered
        title={t("odometer")}
        footer={null}
        onCancel={() => setIsOdometerOpen(false)}
        destroyOnHidden
      >
        <div className="pt-3">
          <InputNumber
            className="common-input"
            min={0}
            value={odometerDraft}
            placeholder={t("odometer")}
            status={odometerDraft !== undefined && odometerDraft < 0 ? "error" : undefined}
            style={{ width: "100%" }}
            onChange={(value) => setOdometerDraft(value ?? undefined)}
          />
          {odometerDraft !== undefined && odometerDraft < 0 && (
            <div className="mt-1 text-xs text-red-500">{t("odometer_min")}</div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <CustomButton
              type="secondary"
              label={t("cancel")}
              onClick={() => setIsOdometerOpen(false)}
            />
            <CustomButton
              type="primary"
              label={t("save")}
              disabled={odometerDraft === undefined || odometerDraft < 0}
              onClick={() => {
                form.setFieldValue("odometer", odometerDraft);
                setIsOdometerOpen(false);
              }}
            />
          </div>
        </div>
      </CustomModal>
    </DetailsCard>
  );
};

export default TechnicalInformationCard;
